import EmptyDottedIcon from '@assets/icons/empty-dotted.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { CatalogSearchErrorCode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/catalog-search-store';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { OrderEntryMobileSkeletonList } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-skeleton-list';
import { useTheme } from '@/contexts/theme-context';

interface OrderEntryMobileEmptyStateProps {
  catalogErrorCode: CatalogSearchErrorCode | null;
  catalogSearchTerm: string;
  hasLoadError: boolean;
  hasNoSearchMatches: boolean;
  isLoading: boolean;
  isReviewMode: boolean;
  onRetryCatalogSearch: () => void;
  onRetryLoad: () => void;
  searchTerm: string;
  showCatalogEmpty: boolean;
  showCatalogError: boolean;
}

export const OrderEntryMobileEmptyState: React.FC<
  OrderEntryMobileEmptyStateProps
> = ({
  catalogErrorCode,
  catalogSearchTerm,
  hasLoadError,
  hasNoSearchMatches,
  isLoading,
  isReviewMode,
  onRetryCatalogSearch,
  onRetryLoad,
  searchTerm,
  showCatalogEmpty,
  showCatalogError,
}) => {
  const theme = useTheme();

  if (hasLoadError) {
    return (
      <ErrorState
        description={i18n.t('sales_portal.order_entry.error.table.description')}
        onPress={onRetryLoad}
        title={i18n.t('sales_portal.order_entry.error.table.title')}
      />
    );
  }

  if (isLoading) {
    return <OrderEntryMobileSkeletonList />;
  }

  if (showCatalogEmpty) {
    return (
      <EmptyState
        description={i18n.t(
          'sales_portal.order_entry.search_empty.description',
        )}
        iconElement={null}
        title={i18n.t('sales_portal.order_entry.catalog_empty.title', {
          query: catalogSearchTerm,
        })}
      />
    );
  }

  if (showCatalogError) {
    return (
      <ErrorState
        description={i18n.t(
          catalogErrorCode === CatalogSearchErrorCode.QueryTooLong
            ? 'sales_portal.order_entry.catalog_search.query_too_long'
            : 'sales_portal.order_entry.catalog_search.error',
        )}
        onPress={onRetryCatalogSearch}
        title={i18n.t('sales_portal.order_entry.error.table.title')}
      />
    );
  }

  if (hasNoSearchMatches) {
    return (
      <EmptyState
        description={i18n.t(
          isReviewMode
            ? 'sales_portal.order_entry.search_empty.review_description'
            : 'sales_portal.order_entry.search_empty.description',
        )}
        iconElement={null}
        title={i18n.t(
          isReviewMode
            ? 'sales_portal.order_entry.search_empty.review_title'
            : 'sales_portal.order_entry.search_empty.title',
          { term: searchTerm.trim() },
        )}
      />
    );
  }

  return (
    <EmptyState
      description={i18n.t(
        isReviewMode
          ? 'sales_portal.order_entry.review.empty.description'
          : 'sales_portal.order_entry.empty.description',
      )}
      iconElement={<EmptyDottedIcon stroke={theme.sk.color.icon.primary} />}
      title={i18n.t(
        isReviewMode
          ? 'sales_portal.order_entry.review.empty.title'
          : 'sales_portal.order_entry.empty.title',
      )}
    />
  );
};
